
'use client';

import { useState } from 'react';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  ScatterChart,
  Scatter,
  Legend
} from 'recharts';
import type { DataGridProps } from '../types';

interface AnalyticsChartsViewProps extends DataGridProps {
  config?: {
    xAxis: string;
    yAxis: string;
    chartType: 'bar' | 'line' | 'area' | 'pie' | 'scatter';
    operation: 'sum' | 'average' | 'count' | 'min' | 'max' | 'unique';
  };
  onConfigChange?: (config: any) => void;
}

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82ca9d', '#ffc658', '#ff7300'];

export const AnalyticsChartsView: React.FC<AnalyticsChartsViewProps> = ({
  dataSource,
  columns,
  gridConfig,
  config,
  onConfigChange
}) => {
  const [selectedChart, setSelectedChart] = useState(config?.chartType || 'bar');

  const numericColumns = columns.filter(col =>
    col.columnType === 'number' ||
    col.columnType === 'currency' ||
    col.columnType === 'percentage'
  );

  const allColumns = columns.filter(col => col.field);

  const xAxisField = config?.xAxis || allColumns[0]?.field;
  const yAxisField = config?.yAxis || numericColumns[0]?.field;

  // Process data for charts with analytics operations
  const processDataWithOperation = () => {
    const operation = config?.operation || 'sum';

    // Group data by X-axis field
    const groupedData = dataSource.reduce((acc, item) => {
      const xValue = String(item[xAxisField!]);
      const yValue = Number(item[yAxisField!]) || 0;

      if (!acc[xValue]) {
        acc[xValue] = [];
      }
      acc[xValue].push(yValue);
      return acc;
    }, {} as Record<string, number[]>);

    // Apply operation to each group
    const processedData = Object.entries(groupedData).map(([xValue, yValues]) => {
      const numericValues = yValues as number[];
      let calculatedValue: number;

      switch (operation) {
        case 'sum':
          calculatedValue = numericValues.reduce((sum: number, val: number) => sum + val, 0);
          break;
        case 'average':
          calculatedValue = numericValues.reduce((sum: number, val: number) => sum + val, 0) / numericValues.length;
          break;
        case 'count':
          calculatedValue = numericValues.length;
          break;
        case 'min':
          calculatedValue = Math.min(...numericValues);
          break;
        case 'max':
          calculatedValue = Math.max(...numericValues);
          break;
        case 'unique':
          calculatedValue = new Set(numericValues).size;
          break;
        default:
          calculatedValue = numericValues.reduce((sum: number, val: number) => sum + val, 0);
      }

      return {
        [xAxisField!]: xValue,
        [yAxisField!]: calculatedValue,
        originalCount: numericValues.length,
        operation: operation
      };
    });

    // Sort by X-axis value and limit to 20 items
    return processedData
      .sort((a, b) => String(a[xAxisField!]).localeCompare(String(b[xAxisField!])))
      .slice(0, 20);
  };

  const chartData: any = processDataWithOperation();

  const handleConfigUpdate = (updates: Partial<typeof config>) => {
    if (onConfigChange && config) {
      onConfigChange({ ...config, ...updates });
    }
  };

  const renderChart = () => {
    const chartType = config?.chartType || selectedChart;

    // Ensure we have valid axis fields
    if (!xAxisField || !yAxisField) {
      return (
        <div className="flex items-center justify-center h-96 text-gray-500">
          <div className="text-center">
            <i className="ri-bar-chart-line text-4xl mb-2"></i>
            <p>Please configure both X-axis and Y-axis columns to display the chart.</p>
          </div>
        </div>
      );
    }

    switch (chartType) {
      case 'line':
        return (
          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e4e7" />
              <XAxis
                dataKey={xAxisField}
                tick={{ fontSize: 12 }}
                angle={-45}
                textAnchor="end"
                height={80}
              />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px'
                }}
              />
              <Line
                type="monotone"
                dataKey={yAxisField}
                stroke="#0088FE"
                strokeWidth={3}
                dot={{ fill: '#0088FE', strokeWidth: 2, r: 4 }}
                activeDot={{ r: 6, stroke: '#0088FE', strokeWidth: 2 }}
              />
            </LineChart>
          </ResponsiveContainer>
        );

      case 'area':
        return (
          <ResponsiveContainer width="100%" height={400}>
            <AreaChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e4e7" />
              <XAxis
                dataKey={xAxisField}
                tick={{ fontSize: 12 }}
                angle={-45}
                textAnchor="end"
                height={80}
              />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px'
                }}
              />
              <Area
                type="monotone"
                dataKey={yAxisField}
                stroke="#00C49F"
                fill="#00C49F"
                fillOpacity={0.6}
              />
            </AreaChart>
          </ResponsiveContainer>
        );

      case 'pie':
        return (
          <ResponsiveContainer width="100%" height={400}>
            <PieChart>
              <Pie
                data={chartData.slice(0, 8)}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(1)}%`}
                outerRadius={120}
                fill="#8884d8"
                dataKey={yAxisField}
                nameKey={xAxisField}
              >
                {chartData.slice(0, 8).map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        );

      case 'scatter':
        return (
          <ResponsiveContainer width="100%" height={400}>
            <ScatterChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e4e7" />
              <XAxis
                dataKey={xAxisField}
                tick={{ fontSize: 12 }}
                type="category"
              />
              <YAxis
                dataKey={yAxisField}
                tick={{ fontSize: 12 }}
                type="number"
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px'
                }}
              />
              <Scatter
                dataKey={yAxisField}
                fill="#FFBB28"
              />
            </ScatterChart>
          </ResponsiveContainer>
        );

      default: // bar
        return (
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e4e7" />
              <XAxis
                dataKey={xAxisField}
                tick={{ fontSize: 12 }}
                angle={-45}
                textAnchor="end"
                height={80}
              />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px'
                }}
              />
              <Bar
                dataKey={yAxisField}
                fill="#0088FE"
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        );
    }
  };

  return (
    <div className={`space-y-6 ${gridConfig.views?.vw_css_class || ''}`}>
      {/* Chart Configuration Controls */}
      <div className="bg-white p-4 sm:p-6 rounded-lg border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Chart Configuration</h3>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              X-Axis Column
            </label>
            <select
              value={config?.xAxis || ''}
              onChange={(e) => handleConfigUpdate({ xAxis: e.target.value })}
              className="w-full p-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-violet-500 focus:border-transparent"
            >
              <option value="">Select column...</option>
              {allColumns.map(col => (
                <option key={col.field} value={col.field}>
                  {col.columnName}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Y-Axis Column
            </label>
            <select
              value={config?.yAxis || ''}
              onChange={(e) => handleConfigUpdate({ yAxis: e.target.value })}
              className="w-full p-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-violet-500 focus:border-transparent"
            >
              <option value="">Select column...</option>
              {numericColumns.map(col => (
                <option key={col.field} value={col.field}>
                  {col.columnName}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Chart Type
            </label>
            <select
              value={config?.chartType || selectedChart}
              onChange={(e) => {
                const newType = e.target.value as any;
                setSelectedChart(newType);
                handleConfigUpdate({ chartType: newType });
              }}
              className="w-full p-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-violet-500 focus:border-transparent"
            >
              <option value="bar">Bar Chart</option>
              <option value="line">Line Chart</option>
              <option value="area">Area Chart</option>
              <option value="pie">Pie Chart</option>
              <option value="scatter">Scatter Plot</option>
            </select>
          </div>

          <div className="flex items-end">
            <button
              onClick={() => window.location.reload()}
              className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 text-sm transition-colors whitespace-nowrap"
            >
              <i className="ri-refresh-line mr-2 w-4 h-4 flex items-center justify-center"></i>
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Main Chart */}
      {(config?.xAxis && config?.yAxis) ? (
        <div className="bg-white p-4 sm:p-6 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                {columns.find(col => col.field === yAxisField)?.columnName} vs {columns.find(col => col.field === xAxisField)?.columnName}
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                {chartData.length} data points • {config.chartType} chart
              </p>
            </div>

            {/* Chart Type Quick Switcher */}
            <div className="flex items-center space-x-2">
              {(['bar', 'line', 'area', 'pie'] as const).map(type => (
                <button
                  key={type}
                  onClick={() => {
                    setSelectedChart(type);
                    handleConfigUpdate({ chartType: type });
                  }}
                  className={`p-2 rounded-md text-sm transition-colors ${(config?.chartType || selectedChart) === type
                    ? 'bg-violet-100 text-violet-700 border border-violet-200'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  title={`${type.charAt(0).toUpperCase() + type.slice(1)} Chart`}
                >
                  <i className={`w-4 h-4 flex items-center justify-center ${type === 'bar' ? 'ri-bar-chart-line' :
                    type === 'line' ? 'ri-line-chart-line' :
                      type === 'area' ? 'ri-area-chart-line' :
                        'ri-pie-chart-line'
                    }`}></i>
                </button>
              ))}
            </div>
          </div>

          <div className="h-96 sm:h-400">
            {renderChart()}
          </div>
        </div>
      ) : (
        <div className="bg-white p-8 rounded-lg border border-gray-200 text-center">
          <i className="ri-bar-chart-line w-12 h-12 flex items-center justify-center mx-auto text-gray-400 mb-4"></i>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Configure Your Chart</h3>
          <p className="text-gray-600">
            Please select both X-axis and Y-axis columns to display the analytics chart.
          </p>
        </div>
      )}

      {/* Chart Statistics */}
      {config?.xAxis && config?.yAxis && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {(() => {
            const values = chartData.map(d => d[yAxisField!]).filter(v => v != null);
            const sum = values.reduce((a, b) => a + b, 0);
            const avg = sum / values.length;
            const max = Math.max(...values);
            const min = Math.min(...values);

            return [
              { label: 'Total', value: sum.toLocaleString(), color: 'blue' },
              { label: 'Average', value: avg.toFixed(1), color: 'green' },
              { label: 'Maximum', value: max.toLocaleString(), color: 'purple' },
              { label: 'Minimum', value: min.toLocaleString(), color: 'orange' },
            ];
          })().map(stat => (
            <div key={stat.label} className="bg-white p-4 rounded-lg border border-gray-200">
              <div className="flex items-center">
                <div className={`w-3 h-3 rounded-full bg-${stat.color}-500 mr-3`}></div>
                <div>
                  <p className="text-sm text-gray-600">{stat.label}</p>
                  <p className="text-lg font-semibold text-gray-900">{stat.value}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
